import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { getItemReservations, type WarehouseStockRow } from '@/api/warehouseStock';

const STAGE_TONE: Record<string, 'success' | 'warning' | 'info' | 'muted'> = {
  'Ready to dispatch': 'success', Packing: 'info', Picked: 'info', Picking: 'warning', 'DO raised': 'muted',
};

const fmtDay = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/** Which orders hold an item's reserved stock, and how much each. */
export function ReservationsModal({ row, onClose }: { row: WarehouseStockRow | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['wh-reservations', row?.itemId],
    queryFn: () => getItemReservations(row!.itemId),
    enabled: !!row,
  });
  const d = q.data;

  return (
    <Modal
      open={!!row}
      onClose={onClose}
      title={row ? `Reserved — ${row.itemName}` : 'Reserved'}
      size="lg"
      footer={<Button variant="glass" onClick={onClose}>Close</Button>}
    >
      {row && (
        <div className="flex flex-col gap-3">
          <p className="text-[12px] text-[var(--text-secondary)]">
            <span className="font-mono">{row.itemCode}</span> · stock is held from the moment a Dispatch Order is raised until its dispatch note is issued
            or the order is cancelled.
          </p>

          {q.isLoading ? (
            <div className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</div>
          ) : q.isError ? (
            <div className="py-8 text-center text-sm text-danger">Couldn’t load the orders for this item.</div>
          ) : !d || d.rows.length === 0 ? (
            <div className="py-8 text-center text-sm text-[var(--text-muted)]">No open orders hold this item.</div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
                <table className="w-full min-w-[560px] text-[12px]">
                  <thead>
                    <tr className="bg-[var(--bg-soft)] text-left text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                      <th className="px-3 py-2">Order</th>
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2">Dispatch Order</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2">Stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r) => (
                      <tr key={r.doId} className="border-t border-[var(--border-color)]">
                        <td className="px-3 py-2">
                          <span className="font-extrabold text-accent">{r.orderRef ?? '—'}</span>
                          {r.online && <span className="ml-1 text-[10px] font-semibold text-[var(--text-muted)]">Online</span>}
                        </td>
                        <td className="px-3 py-2 font-semibold">{r.customer ?? '—'}</td>
                        <td className="px-3 py-2">
                          <div>{r.doNo}</div>
                          <div className="text-[10px] text-[var(--text-muted)]">{fmtDay(r.doDate)}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-bold tabular-nums">{r.qty}</td>
                        <td className="px-3 py-2"><Badge tone={STAGE_TONE[r.stage] ?? 'muted'}>{r.stage}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-[var(--border-color)] bg-[var(--bg-soft)] font-bold">
                      <td className="px-3 py-2" colSpan={3}>{d.rows.length} order{d.rows.length === 1 ? '' : 's'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{d.totalOnOrders}</td>
                      <td className="px-3 py-2" />
                    </tr>
                  </tfoot>
                </table>
              </div>
              {Math.abs(d.totalOnOrders - d.reservedQty) > 1e-6 && (
                <p className="text-[11px] font-semibold text-warning">
                  Reserved shows {d.reservedQty}, but open orders hold {d.totalOnOrders}. The difference isn’t tied to any open order.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
